import { useEffect } from "react";

import KickClient from "~/visits/utils/api.js";
import {
  isInPendingEditFlow,
  isPendingEditExpired,
} from "~/visits/utils/pendingVisitUtils";
import {
  clearEditPendingVisitSession,
  getEditPendingVisitExpiresAt,
} from "~/visits/utils/visitCookies";

// expireEditSession cleans up the patient's edit-mode state and redirects to /me/portal.
// It best-effort releases the system patient lock — if the unlock fails, we still continue
// because the backend lock auto-expires at the same 15-minute mark anyway.
export function expireEditSession(checkoutSessionID) {
  try {
    KickClient.newAuthenticatedClient()
      .post("/visits/edit-pending/unlock", {})
      .catch(() => {});
  } catch (err) {
    // ignore — best-effort
  }
  if (checkoutSessionID) {
    clearEditPendingVisitSession(checkoutSessionID);
  }
  window.location.href = "/me/portal";
}

// armPendingEditTimer is the class-component analog of usePendingEditTimeout. Call it from
// componentDidMount and call the returned cleanup function from componentWillUnmount.
export function armPendingEditTimer(checkoutSessionID) {
  if (!checkoutSessionID) return () => {};
  if (!isInPendingEditFlow(checkoutSessionID)) return () => {};
  if (isPendingEditExpired(checkoutSessionID)) {
    expireEditSession(checkoutSessionID);
    return () => {};
  }
  const expiresAt = getEditPendingVisitExpiresAt(checkoutSessionID);
  const timerID = setTimeout(
    () => expireEditSession(checkoutSessionID),
    Math.max(0, expiresAt - Date.now()),
  );
  return () => clearTimeout(timerID);
}

// usePendingEditTimeout arms a 15-minute force-redirect when the patient is in an active
// "Edit Pending Visit" flow. Mount it from every edit-flow page (select-pharmacy, num-packs,
// flavor pickers, order-details). The deadline lives in localStorage so refreshing a tab keeps
// the original countdown intact.
export default function usePendingEditTimeout(checkoutSessionID) {
  useEffect(() => armPendingEditTimer(checkoutSessionID), [checkoutSessionID]);
}
